<template>
    <div class="datagrid text-start">
        <div class="row hidden-print">
            <div class="col-xs-12">
                <div class="toolbar">
                    <div class="tool-buttons">
                        <div class="btn-group">
                            <button v-bind="tool.attr"
                                    v-for="tool in toolbarButtons" :key="tool.id"
                                    @click="clickTool(tool)"
                                    :class="['btn', tool.classes]"
                                    v-html="tool.html"></button>
                        </div>
                    </div>
                    <div class="search-box-container ms-auto" ref="search-box-container">
                        <div class="dropdown">
                            <div :class="{'input-group': showAdvanced}">
                                <input v-model="searchTerms" @search="search" incremental type="search" placeholder="Search" :class="'form-control search-box ' + textClass" maxlength="50">
                                <button v-if="showAdvanced" class="btn btn-light" @click="advancedsearch = !advancedsearch"><i :class="{fa:true, 'fa-chevron-down':!advancedsearch, 'fa-chevron-up':advancedsearch}"></i></button>
                            </div>
                            <div class="advanced-search-container">
                                <div :class="{ 'dropdown-menu dropdown-menu-large row advanced-search': true, 'd-none': !advancedsearch }">
                                    <slot name="advanced-search"></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="pagingcontrols" class="form-inline paging-tools">
                        <span>Show </span>
                        <select v-model="query.size" :class="'form-control ' + textClass">
                            <option v-for="s in pagesizes" :key="s">{{ s }}</option>
                        </select><span> rows</span>

                        <nav v-if="rowCount > query.size" class="navbar navbar-expand-xl pagination-container">
                            <ul class="collapse navbar-collapse pagination">
                                <li v-if="query.page > 1" class="page-item">
                                    <a :class="'page-link ' + textClass" href="#" @click.prevent="nextPage(-1)">Prev</a>
                                </li>
                                <li v-for="page in paging" :class="{'page-item': true, active: page.idx === query.page, disabled: !page.idx }" :key="page.key">
                                    <a :class="'page-link ' + textClass" v-if="page.idx === query.page">{{ page.idx }}</a>
                                    <a :class="'page-link ' + textClass" v-if="page.idx != query.page && page.idx" href="#" @click.prevent="showPage(page.idx)">{{ page.idx }}</a>
                                    <span v-if="!page.idx" :class="'page-link separator ' + textClass">...</span>
                                </li>
                                <li v-if="query.page < pageCount" class="page-item">
                                    <a :class="'page-link ' + textClass" href="#" @click.prevent="nextPage(1)">Next</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <table :class="['table', tableClass]">
                <thead>
                    <tr>
                        <th v-for="col in gridColumns" :key="col.name" :class="[col.classes, col.headerClass]" :style="col.headerstyle">
                            <a v-if="col.sortable !== false" href="" 
                               v-bind="col.headerAttr"
                               :class="{sortable: true, sorted: query.sort === col.name, asc: query.sort === col.name && query.dir === 'asc'}"
                               @click.prevent="sortColumn(col.name)">{{ col.headerLabel || col.label || col.name }}</a>
                            <span v-else v-bind="col.headerAttr">{{ col.headerLabel || col.label || col.name }}</span>
                        </th>
                        <th v-if="inlineButtons.length"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!rows.length">
                        <td :colspan="gridColumns.length + (inlineButtons.length ? 1 : 0)">
                            <div>
                                <h3 class="text-muted text-center">No results found</h3>
                            </div>
                        </td>
                    </tr>
                    <tr v-for="entry in rows" :key="entry[identity]" :class="{'active': selectable && selected[identity] === entry[identity]}" @click="selectRow(entry)">
                        <td v-for="col in gridColumns" :class="[col.classes, col.cellClass]" :key="col.name" :style="col.style"><div :class="col.innerClass" v-if="col.renderer" v-html="Column.formattedValue(entry, col)"></div><div v-if="!col.renderer" :class="col.innerClass">{{ Column.formattedValue(entry, col) }}</div></td>
                        <td v-if="inlineButtons.length" class="pb-1 pt-1 text-end">
                            <div class="btn-group btn-group-sm">
                                <button v-bind="tool.attr"
                                        v-for="tool in inlineButtons.filter(b => !b.visibleWhen || b.visibleWhen(entry))"
                                        :key="tool.id"
                                        @click="clickTool(tool, entry)"
                                        :class="['btn', tool.classes]"
                                        v-html="tool.html"></button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
    <!-- Modals -->
    <div class="modal fade" ref="addModal" :id="addModalId" data-bs-backdrop="static" data-bs-keyboard="true" tabindex="-1" aria-hidden="true">
        <div :class="'modal-dialog modal-dialog-centered ' + modalSize">
            <div class="modal-content text-dark text-start">
                <div class="modal-header">
                    <h5 class="modal-title">Add {{ title }}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="loading"></button>
                </div>
                <div class="modal-body">
                    <auto-form ref="addForm" :columns="columns.filter(c => c.editable != false)" :column-count="modalCols" :loading="loading" @onUpload="uploadFile"></auto-form>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-default" data-bs-dismiss="modal" :disabled="loading"> Cancel </button>
                    <button type="button" class="btn btn-primary" @click="submitAdd" :disabled="loading"> Add </button>
                </div>
            </div>
        </div>
    </div>
    <div class="modal fade" ref="delModal" :id="delModalId" data-bs-backdrop="static" data-bs-keyboard="true" tabindex="-1" aria-hidden="true" :onfocus="deleteModalFocus">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content text-dark text-start">
                <div class="modal-header">
                    <h5 class="modal-title">Delete {{ title }}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <h6>Are you sure you want to delete this?</h6>
                    <p class="m-3">{{ editingItem[descriptor] }}</p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-default" data-bs-dismiss="modal"> Cancel </button>
                    <button type="button" class="btn btn-danger" @click="submitDelete"> Delete </button>
                </div>
            </div>
        </div>
    </div>
    <div class="modal fade" ref="editModal" :id="editModalId" data-bs-backdrop="static" data-bs-keyboard="true" tabindex="-1" aria-hidden="true">
        <div :class="'modal-dialog modal-dialog-centered ' + modalSize">
            <div class="modal-content text-dark text-start">
                <div class="modal-header">
                    <h5 class="modal-title">Edit {{ title }}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="loading"></button>
                </div>
                <div class="modal-body">
                    <auto-form ref="editForm" :columns="columns" :column-count="modalCols" :loading="loading" @onUpload="uploadFile"></auto-form>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-default" data-bs-dismiss="modal" :disabled="loading"> Cancel </button>
                    <button type="button" class="btn btn-primary" @click="submitEdit" :disabled="loading"> Save </button>
                </div>
            </div>
        </div>
    </div>
    <div class="modal fade" ref="importModal" :id="importModalId" data-bs-backdrop="static" data-bs-keyboard="true" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content text-dark text-start">
                <div class="modal-header">
                    <h5 class="modal-title">Import {{ title }}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="loading"></button>
                </div>
                <div class="modal-body">
                    <p>Select a file to upload and import the records. Use the provided template as a starting point for this record type.</p>
                    <button class="btn text-primary w-100 mb-3" @click="downloadTemplate"><i class="fa fa-download"></i> Get template</button>
                    <form ref="importForm">
                        <div class="input-group mb-3">
                            <input type="text" class="form-control" placeholder="Select Excel file" name="filename" :value="importFilename" readonly>
                            <label class="btn btn-outline-secondary">
                                Browse <input ref="importFile" type="file" name="importFile" hidden="" style="display:none" @change="onSelectedImportfile">
                            </label>
                        </div>
                    </form>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-default" data-bs-dismiss="modal" :disabled="loading"> Cancel </button>
                    <button type="button" class="btn btn-primary" @click="submitImport" :disabled="loading"> Upload </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import _ from 'lodash';
    import Prefs from '../../common/preferences';
    import axios from 'axios';
    import { DateTime } from 'luxon';
    import { ObjectFormData } from '../../common/ObjectFormData';
    import qs from 'qs';
    import AutoForm from './AutoForm';
    import { Column } from './AutoForm';

    var t_search, lastMouseDownElement, datagridId = parseInt(Math.random() * 10000000);
    
    const modals = {
        get ID() { return datagridId; },
        get add() { return 'add' + this.ID; },
        get del() { return 'del' + this.ID; },
        get edit() { return 'edit' + this.ID; },
        get import() { return 'import' + this.ID; },
    }

    export default {
        emits: ['onselected', 'url'],
        props: {
            columns: {
                type: Array,
                default: () => [{
                    name: '',
                    label: '',
                    classes: '',
                    sortable: true,
                    editable: true,
                }]
            },
            customTools: {
                type: Array
            },
            dataendpoint: String,
            defaultsort: String,
            defaultsortDir: String,
            descriptor: {
                type: String,
                default: () => 'title'
            },
            filter: {
                type: Object,
                default: () => { }
            },
            identity: {
                type: String,
                default: () => 'id'
            },
            modalSize: String,
            modalCols: {
                type: Number,
                default: () => 1
            },
            pagingcontrols: {
                type: Boolean,
                default: () => true
            },
            pagesizes: {
                type: Array,
                default: () => [10, 25, 50, 100, 200]
            },
            selectable: {
                type: Boolean,
                default: () => true
            },
            showAdvanced: {
                type: Boolean,
                default: () => true
            },
            showExport: {
                type: Boolean,
                default: () => false
            },
            showImport: {
                type: Boolean,
                default: () => false
            },
            title: {
                type: String,
                default: '',
            },
            tools: {
                type: Array,
                default: () => []
            },
            toolAdd: {
                type: Boolean,
                default: () => true
            },
            toolEdit: {
                type: Boolean,
                default: () => true
            },
            toolDelete: {
                type: Boolean,
                default: () => true
            },
        },
        data: function () {
            return {
                addModalId: modals.add,
                advancedsearch: false,
                advancedSearchParams: {},
                delModalId: modals.del,
                editModalId: modals.edit,
                editingItem: {},
                importFilename: '',
                importModalId: modals.import,
                lastRefresh: 0,
                loading: true,
                paging: [],
                pageCount: 0,
                query: {
                    page: 1,
                    size: 10,
                    sort: '',
                    dir: 'desc',
                    q: '',
                },
                rows: [],
                rowCount: 0,
                searchTerms: '',
                selected: {},
                Column,
            };
        },
        components: {
            AutoForm
        },
        computed: {
            toolbarButtons: function () {
                return this.tools.filter(t => (!t.on) || (t.on === 'toolbar'));
            },
            inlineButtons: function () {
                return this.tools.filter(t => t.on === 'row');
            },
            gridColumns: function () {
                return this.columns.filter(c => !c.hidden || (Array.isArray(c.hidden) && !c.hidden.includes('grid')));
            },
            tableClass: function () { return Prefs.theme.value ? 'table-striped table-dark' : 'table-striped' },
            textClass: function () { return Prefs.theme.value ? 'text-bg-dark' : '' },
        },
        methods: {
            addDialog(data) {
                this.clickTool({ id: 'add' }, data);
                bootstrap.Modal.getOrCreateInstance(this.$refs.addModal).show();
            },
            clickTool(tool, row) {
                const vm = this;
                if (tool.click) {
                    tool.click(row, tool.id);
                }
                switch (tool.id) {
                    case 'add':
                        vm.$refs.addForm.clear(row);
                        break;
                    case 'delete':
                    case 'edit':
                        vm.editingItem = _.cloneDeep(row);
                        _.merge(vm.editingItem, vm.filter);
                        vm.$refs.editForm.model = vm.editingItem;
                        break;
                    case 'export':
                        var data = Object.assign({}, vm.query);
                        _.merge(data, vm.filter);
                        window.open(vm.dataendpoint + '/export?' + qs.stringify(data), '_self');
                        break;
                }
            },
            deleteModalFocus(event) {
                event.target.querySelector('.btn-danger').focus();
            },
            downloadTemplate() {
                window.open(this.dataendpoint + '/template', '_self');
            },
            getUrl(name, url) {
                let item = _.isEmpty(this.editingItem) ? this.selected : this.editingItem;
                let args = { name, url, item };
                this.$emit('url', args)
                return args.url;
            },
            onSelectedImportfile() {
                this.importFilename = this.$refs.importFile?.value?.replace(/\\/g, '/').replace(/.*\//, '');
            },
            selectRow(item) {
                if (this.selected[this.identity] != item[this.identity]) {
                    this.selected = item;
                    this.$emit('onselected', item);
                }
            },
            submitAdd() {
                const vm = this;
                let form = vm.$refs.addForm;
                let data = _.cloneDeep(form.model);

                _.merge(data, vm.filter);
                vm.columns.forEach(col => {
                    if (col.editable === false) delete data[col.name];
                });

                vm.loading = true;
                axios.post(vm.getUrl('add', vm.dataendpoint), data)
                    .then(
                        response => {
                            if (response.status === 200 || response.status === 201) {
                                vm.getData(null, true);
                                bootstrap.Modal.getInstance(vm.$refs.addModal).hide();
                                vm.editingItem = {};
                            } else {
                                vm.showError(response.data?.message);
                            }
                        },
                        err => {
                            vm.showError(err.response?.data?.message);
                        }
                    )
                    .catch(err => {
                        vm.showError(err.response?.data?.message);
                    })
                    .finally(() => {
                        vm.loading = null;
                    });
            },
            submitDelete() {
                const vm = this;

                vm.loading = true;
                axios.delete(vm.getUrl('delete', vm.dataendpoint + '/' + vm.editingItem[vm.identity]))
                    .then(
                        response => {
                            if (response.status === 200) {
                                vm.getData(null, true);
                                bootstrap.Modal.getInstance(vm.$refs.delModal).hide();
                                vm.editingItem = {};
                                vm.selectRow({});
                            } else {
                                vm.showError(response.data?.message);
                            }
                        },
                        err => {
                            vm.showError(err.response?.data?.message);
                        }
                    )
                    .catch(err => {
                        vm.showError(err.response?.data?.message);
                    })
                    .finally(() => {
                        vm.loading = null;
                    });
            },
            submitEdit() {
                const vm = this;
                let form = vm.$refs.editForm;
                let data = _.cloneDeep(form.model);

                vm.columns.forEach(col => {
                    if (col.editable === false) delete data[col.name];
                });

                vm.loading = true;
                axios.patch(vm.getUrl('update', vm.dataendpoint + '/' + vm.editingItem[vm.identity]), data)
                    .then(
                        response => {
                            if (response.status === 200) {
                                if (response.data?.errors) {
                                    form.errors = response.data.errors;
                                } else {
                                    vm.getData(null, true);
                                    bootstrap.Modal.getInstance(vm.$refs.editModal).hide();
                                    vm.editingItem = {};
                                    form.model = vm.editingItem;
                                }
                            } else {
                                vm.showError(response.data?.message);
                            }
                        },
                        err => {
                            vm.showError(err.response?.data?.message);
                        }
                    )
                    .catch(err => {
                        vm.showError(err.response?.data?.message);
                    })
                    .finally(() => {
                        vm.loading = null;
                    });
            },
            submitImport() {
                const vm = this;
                let form = vm.$refs.importForm;
                let data = ObjectFormData.createFormData(form)
                _.forOwn(vm.filter, (v, k) => { data.set(k, v); });

                vm.loading = true;
                axios.post(vm.getUrl('import', vm.dataendpoint + '/import'), data, { headers: { 'Content-Type': 'multipart/form-data' } })
                    .then(
                        response => {
                            if (response.status === 200) {
                                if (response.data?.errors) {
                                    form.errors = response.data.errors;
                                } else {
                                    vm.getData(null, true);
                                    bootstrap.Modal.getInstance(vm.$refs.importModal).hide();
                                }
                            } else {
                                vm.showError(response.data?.message);
                            }
                        },
                        err => {
                            vm.showError(err.response?.data?.message);
                        }
                    )
                    .catch(err => {
                        vm.showError(err.response?.data?.message);
                    })
                    .finally(() => {
                        vm.loading = null;
                    });
            },
            uploadFile(event, model, col) {
                const vm = this;
                let data = ObjectFormData.createFormData(event.target.form);
                _.forOwn(model, (v, k) => { data.set(k, v); });

                vm.loading = true;
                axios.post(vm.getUrl('upload', vm.dataendpoint + '/upload'), data, { headers: { 'Content-Type': 'multipart/form-data' } })
                    .then(
                        response => {
                            if (response.status === 200) {
                                model[col.name] = response.data.model.url;
                                if (col.onUploaded) col.onUploaded(model, response.data);
                            } else {
                                vm.showError(response.data?.message);
                            }
                        },
                        err => {
                            vm.showError(err.response?.data?.message);
                        }
                    )
                    .catch(err => {
                        vm.showError(err.response?.data?.message);
                    })
                    .finally(() => {
                        vm.loading = null;
                        event.target.value = '';
                    });
            },
            search(event) {
                this.query.page = 1;
                this.getData();
            },
            searchTimeout() {
                clearTimeout(t_search);
                t_search = setTimeout(this.search, 500);
            },
            sortColumn(column) {
                const vm = this;
                if (vm.query.sort === column) {
                    vm.query.dir = vm.query.dir === 'asc' ? 'desc' : 'asc';
                } else {
                    vm.query.dir = 'desc';
                }
                vm.query.sort = column;
                vm.query.page = 1;
                vm.getData();
            },
            nextPage(i) {
                this.showPage(this.query.page + i);
            },
            showPage(p) {
                this.query.page = p;
                this.getData();
            },
            getData(search, nocache) {
                const vm = this;
                vm.loading = true;
                if (search) {
                    vm.advancedSearchParams = search;
                    if (!_.isEmpty(search)) {
                        vm.advancedsearch = false;
                    }
                }
                let params = vm.getParams();
                vm.loading = true;
                if (nocache) {
                    _.merge(params, { _t: DateTime.local().valueOf() });
                }
                axios.get(vm.dataendpoint, { params })
                    .then(
                        response => {
                            if (response.status === 200) {
                                vm.refresh(response.data);
                            } else {
                                vm.showError(response.data?.message);
                            }
                        },
                        err => {
                            vm.showError(err.response?.data?.message);
                        }
                    )
                    .catch(err => {
                        vm.showError(err.response?.data?.message);
                    })
                    .finally(() => {
                        vm.loading = null;
                    });
            },
            getParams() {
                let params = {};
                _.merge(params, this.query);
                _.merge(params, this.filter);
                _.merge(params, this.advancedSearchParams);
                params.q = this.searchTerms;
                return params;
            },
            refresh(data) {
                const vm = this;
                vm.lastRefresh = Date.now();
                if (data.rows) {
                    vm.rows = data.rows;
                }
                _.merge(vm.query, data.query);
                vm.rowCount = data.count;
                let pageCount = Math.ceil(vm.rowCount / vm.query.size);
                let startAt = Math.max(2, pageCount > 6 ? vm.query.page - 2 : 2);
                let endAt = Math.min(pageCount, startAt + 4);
                let paging = [{ idx: 1, key: 0 }];
                if (startAt > 2) {
                    paging.push({ idx: ((startAt === 3) ? 2 : 0), key: paging.length });
                }
                for (var i = startAt; i <= endAt; i++) {
                    paging.push({ idx: i, key: paging.length });
                }
                if (paging.at(-1).idx < pageCount - 1) {
                    paging.push({ idx: 0, key: paging.length });
                }
                if (paging.at(-1).idx < pageCount) {
                    paging.push({ idx: pageCount, key: paging.length });
                }
                vm.paging = paging;
                vm.pageCount = pageCount;
                if (vm.selected) {
                    const id = vm.identity;
                    vm.rows.forEach(r => {
                        if (r[id] === vm.selected[id]) {
                            Object.assign(vm.selected, r);
                            vm.$emit('onselected', vm.selected);
                        }
                    });
                }
            },
            advancedSearch(data) {
                this.query.page = 1;
                this.getData(data);
            },
            showError(message) {
                Events.send(Events.error, message || 'An error occurred while loading data. Please try again later.');
            },
            onmousedown(event) {
                const vm = this;
                lastMouseDownElement = event.target;
            },
            onblur() {
                const vm = this;
                setTimeout(() => {
                    let container = vm.$refs['search-box-container'];
                    if (container && !container.contains(lastMouseDownElement)) {
                        if (!document.activeElement || (!container.contains(document.activeElement))) {
                            vm.advancedsearch = false;
                        }
                    }
                }, 0);
            },
            onvisibilitychange(e) {
                if (!this.loading && 'visible' === `${e.target.visibilityState}` && this.lastRefresh + 10000 < Date.now()) {
                    this.getData();
                }
            }
        },
        watch: {
            'query.size': function (newValue, oldValue) {
                if (this.query && newValue != oldValue) {
                    this.query.page = 1;
                    this.getData();
                }
            },
            filter: function (newValue, oldValue) {
                if (!_.isEqual(newValue, oldValue)) {
                    this.getData();
                }
            },
        },
        mounted() {
            const vm = this;
            vm.query.sort = vm.defaultsort || vm.columns[0].name;
            vm.query.dir = vm.defaultsortDir || 'asc';
            vm.getData();
            const b = document.body;
            b.onmousedown = vm.onmousedown;
            b.onblur = vm.onblur;
            document.addEventListener('visibilitychange', vm.onvisibilitychange);
            b.appendChild(vm.$refs.addModal);
            b.appendChild(vm.$refs.delModal);
            b.appendChild(vm.$refs.editModal);
            if (vm.showImport)
                b.appendChild(vm.$refs.importModal);
        },
        setup(self) {
            datagridId++;
            if (self.tools.length === 0) {
                /* eslint-disable vue/no-mutating-props */
                self.toolEdit && self.tools.push(
                    { id: 'edit', classes: 'btn-outline-secondary text-primary', attr: { title: 'Edit', 'data-bs-toggle': 'modal', 'data-bs-target': '#' + modals.edit }, html: '<i class="fa fa-pencil"></i>', on: 'row' }
                );
                self.toolDelete && self.tools.push(
                    { id: 'delete', classes: 'btn-outline-secondary text-danger', attr: { title: 'Delete', 'data-bs-toggle': 'modal', 'data-bs-target': '#' + modals.del }, html: '<i class="fa fa-trash"></i>', on: 'row' }
                );
                let i = 0;
                self.customTools?.forEach(tool => {
                    self.tools.splice(i++, 0, tool);
                });
                self.showImport && self.tools.splice(0, 0,
                    { id: 'import', classes: 'btn-outline-success', attr: { 'data-bs-toggle': 'modal', 'data-bs-target': '#' + modals.import }, html: '<i class="d-none d-sm-inline fa fa-upload"></i> Import' },
                );
                self.showExport && self.tools.splice(0, 0,
                    { id: 'export', classes: 'btn-outline-success', html: '<i class="d-none d-sm-inline fa fa-download"></i> Export' },
                );
                self.toolAdd && self.tools.splice(0, 0,
                    { id: 'add', classes: 'btn-success', attr: { 'data-bs-toggle': 'modal', 'data-bs-target': '#' + modals.add }, html: '<i class="d-none d-sm-inline fa fa-plus"></i> Add' }
                );
            }
        }
    };
</script>
